import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Link } from 'react-router-dom';
import UserAvatar from '../util/UserAvatar';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import AlarmOutlinedIcon from '@mui/icons-material/AlarmOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import UndoIcon from '@mui/icons-material/Undo';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FeedRow, { Tone } from './FeedRow';

// ── Shared types (FeedCard builds these from the existing endpoints) ─────────
export type ApKind = 'leave' | 'sickness' | 'lateness' | 'expense' | 'mileage';
export interface Approval {
  key: string; kind: ApKind; name: string; what: string; sub: string; ts: number | null; userId?: number;
  absenceId?: number; expenseId?: number; carId?: number;
  receiptUrl?: string | null;
}
export type ApState = 'approved' | 'rejected' | 'busy';
export interface TsEntry {
  id: number; userId: number; name: string; date: string; week: string; di: number;
  start: string; end: string; minutes: number; category: string; task: string; description: string;
}
export type EntryState = 'approved' | 'returned' | 'busy';
export interface Person { id: number; first_name?: string; last_name?: string; avatarUrl?: string | null; contract?: number | null; }

interface Props {
  entries: TsEntry[];
  entryState: Record<number, EntryState>;
  others: Approval[];
  apState: Record<string, ApState>;
  people: Record<number, Person>;
  showTs: boolean; showLv: boolean; showEx: boolean;
  baseUrl: string;
  onApproveEntries: (ids: number[]) => Promise<void>;
  onReturnEntries: (ids: number[], comment: string, source: string, ownerId: number) => Promise<void>;
  onApprove: (ap: Approval) => Promise<void>;
  onReject: (ap: Approval, reason: string) => Promise<void>;
  title?: string;          // All tab: group heading above the sections
  after?: React.ReactNode; // All tab: the rest of the feed, in the same scroll under the sections
}

type Scope = 'w' | 'd' | 'e';
interface Group { gk: string; userId: number; week: string; es: TsEntry[]; }

const DN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const f1 = (n: number) => String(+n.toFixed(1));
const hrs = (es: TsEntry[]) => es.reduce((s, e) => s + e.minutes, 0) / 60;
const hm = (min: number) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
const LEAVE_KINDS: ApKind[] = ['leave', 'sickness', 'lateness'];
const OTHER_ICON: Record<string, typeof EventOutlinedIcon> = {
  leave: EventOutlinedIcon, sickness: MedicalServicesOutlinedIcon, lateness: AlarmOutlinedIcon,
  expense: ReceiptLongOutlinedIcon, mileage: DirectionsCarOutlinedIcon,
};
const OTHER_LABEL: Record<string, string> = {
  leave: 'Leave', sickness: 'Sickness', lateness: 'Lateness', expense: 'Expense claim', mileage: 'Mileage claim',
};
// "Open in Approvals" = Timesheets → Approvals tab, filtered to the employee
const approvalsUrl = (userId?: number) => `/timesheets?tab=approvals${userId ? `&user=${userId}` : ''}`;

const FeedApprovals: React.FC<Props> = ({
  entries, entryState, others, apState, people, showTs, showLv, showEx, baseUrl,
  onApproveEntries, onReturnEntries, onApprove, onReject, title, after,
}) => {
  // All sections start collapsed; they open only on a header click
  const [sec, setSec] = useState({ ts: false, lv: false, ex: false });
  const [pop, setPop] = useState<{ gk: string; di: number } | null>(null);
  const [ret, setRet] = useState<{ sc: Scope; id: string; text: string; bad: boolean } | null>(null);
  const [rejecting, setRejecting] = useState<{ key: string; reason: string } | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  const stOf = (e: TsEntry) => entryState[e.id];
  const pend = useCallback((es: TsEntry[]) => es.filter(e => !entryState[e.id]), [entryState]);
  const otherPending = (o: Approval) => !apState[o.key] || apState[o.key] === 'busy';
  const person = (uid: number, fallback: string): Person => {
    const p = people[uid];
    if (p) return p;
    const [first_name = '', ...rest] = fallback.split(' ');
    return { id: uid, first_name, last_name: rest.join(' ') };
  };
  const nameOf = (uid: number, fallback: string) => {
    const p = people[uid];
    return p ? `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim() || fallback : fallback;
  };

  // ── Groups: one per employee per ISO week; a person's weeks stay together ──
  const groups = useMemo(() => {
    const m = new Map<string, Group>();
    entries.forEach(e => {
      const gk = `${e.userId}|${e.week}`;
      if (!m.has(gk)) m.set(gk, { gk, userId: e.userId, week: e.week, es: [] });
      m.get(gk)!.es.push(e);
    });
    // Every week row stands alone: oldest week first, then by name
    return Array.from(m.values()).sort((a, b) => a.week.localeCompare(b.week) || a.es[0].name.localeCompare(b.es[0].name));
  }, [entries]);

  const leaves = useMemo(() => others.filter(o => LEAVE_KINDS.includes(o.kind)), [others]);
  const claims = useMemo(() => others.filter(o => !LEAVE_KINDS.includes(o.kind)), [others]);

  const n = {
    ts: showTs ? pend(groups.flatMap(g => g.es)).length : 0,
    lv: showLv ? leaves.filter(otherPending).length : 0,
    ex: showEx ? claims.filter(otherPending).length : 0,
  };

  // A section whose last item is done collapses
  const prevN = useRef(n);
  useEffect(() => {
    const p = prevN.current;
    setSec(s => {
      const next = { ...s };
      (Object.keys(n) as (keyof typeof n)[]).forEach(k => {
        if (p[k] > 0 && n[k] === 0) next[k] = false;
      });
      return next;
    });
    prevN.current = n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [n.ts, n.lv, n.ex]);


  const gFind = (gk: string) => groups.find(g => g.gk === gk);
  const dayEs = (g: Group, di: number) => g.es.filter(e => e.di === di).sort((a, b) => a.start.localeCompare(b.start));
  const dayDate = (week: string, di: number) => moment(week).add(di, 'day');

  // ── Actions ────────────────────────────────────────────────────────────────
  const scopeEntries = (sc: Scope, id: string): TsEntry[] => {
    if (sc === 'e') return entries.filter(e => String(e.id) === id);
    const [gk, di] = id.split('#');
    const g = gFind(gk);
    if (!g) return [];
    return sc === 'd' ? dayEs(g, Number(di)) : g.es;
  };

  const approveScope = (sc: Scope, id: string) => {
    const ids = pend(scopeEntries(sc, id)).map(e => e.id);
    if (ids.length) onApproveEntries(ids);
    if (ret && ret.sc !== 'e') setRet(null);
  };

  const sendReturn = () => {
    if (!ret) return;
    const text = ret.text.trim();
    if (!text) { setRet({ ...ret, bad: true }); return; }
    const es = pend(scopeEntries(ret.sc, ret.id));
    if (!es.length) { setRet(null); return; }
    // Notice source mirrors PendingApprovalsPanel; one notice per returned day/week
    const first = es[0];
    const source = ret.sc === 'e'
      ? `Reject: date: ${moment(first.date).format('DD.MM.YYYY')}, ${first.start}-${first.end} ${first.category}, ${hm(first.minutes)}`
      : ret.sc === 'd'
        ? `Reject: date: ${moment(first.date).format('DD.MM.YYYY')}, ${es.length} ${es.length === 1 ? 'entry' : 'entries'}`
        : `Reject: week: w/c ${moment(first.week).format('D MMM YYYY')}, ${es.length} ${es.length === 1 ? 'entry' : 'entries'}`;
    onReturnEntries(es.map(e => e.id), text, source, first.userId);
    setRet(null);
  };

  const approveSection = (k: 'ts' | 'lv' | 'ex') => {
    if (k === 'ts') {
      const ids = pend(groups.flatMap(g => g.es)).map(e => e.id);
      if (!ids.length || !window.confirm(`Approve ${ids.length} time ${ids.length === 1 ? 'entry' : 'entries'}?`)) return;
      setPop(null); setRet(null);
      onApproveEntries(ids);
      return;
    }
    const list = (k === 'lv' ? leaves : claims).filter(otherPending);
    if (!list.length || !window.confirm(`Approve ${list.length} ${k === 'lv' ? 'leave request' : 'claim'}${list.length === 1 ? '' : 's'}?`)) return;
    list.forEach(o => onApprove(o));
  };

  // ── Popover: inside the card, below the cell if it fits, else above ───────
  const placePop = useCallback(() => {
    const el = popRef.current;
    if (!pop || !el) return;
    const cell = scrollRef.current?.querySelector(`[data-fdcell="${pop.gk}#${pop.di}"]`) as HTMLElement | null;
    const card = el.closest('.fd-card') as HTMLElement | null;
    if (!cell || !card) return;
    const cr = card.getBoundingClientRect(), r = cell.getBoundingClientRect(), W = el.offsetWidth;
    el.style.maxHeight = 'none';
    const full = el.offsetHeight;
    const below = cr.bottom - r.bottom - 14, above = r.top - cr.top - 14;
    const down = full <= below || (full > above && below >= above);
    const H = Math.max(120, Math.min(full, down ? below : above));
    const left = Math.max(8, Math.min(r.left - cr.left + r.width / 2 - W / 2, cr.width - W - 8));
    const top = down ? r.bottom - cr.top + 6 : r.top - cr.top - 6 - H;
    el.style.maxHeight = `${H}px`;
    el.style.left = `${left}px`;
    el.style.top = `${Math.max(8, Math.min(top, cr.height - H - 8))}px`;
  }, [pop]);

  useLayoutEffect(placePop, [placePop, entries, entryState, ret]);

  useEffect(() => {
    if (!pop) return;
    const sc = scrollRef.current;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (popRef.current?.contains(t) || t.closest('[data-fdcell]')) return;
      setPop(null); setRet(r => (r && r.sc !== 'w' ? null : r));
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { setPop(null); setRet(null); } };
    sc?.addEventListener('scroll', placePop, { passive: true });
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      sc?.removeEventListener('scroll', placePop);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [pop, placePop]);

  // Close the popover when its group disappears (after a reload)
  useEffect(() => { if (pop && !gFind(pop.gk)) setPop(null); });

  // ── Fragments ──────────────────────────────────────────────────────────────
  const statOf = (es: TsEntry[]) => {
    const p = pend(es).length, r = es.filter(e => stOf(e) === 'returned').length;
    if (p === es.length) return 'amber';
    if (p === 0 && r === 0) return 'green';
    if (p === 0) return 'red';
    return 'blue';
  };

  const rbox = (sc: Scope, id: string, who: string) => {
    if (!ret || ret.sc !== sc || ret.id !== id) return null;
    const what = sc === 'w' ? 'the whole week' : sc === 'd' ? 'this day' : 'this entry';
    return (
      <div className="fd-rbox">
        <div className="fd-rcol">
          <div className="fd-rto">Return {what} · the comment is sent to {who} as a notification</div>
          <textarea autoFocus className={ret.bad ? 'bad' : ''} value={ret.text}
            placeholder={ret.bad ? 'A comment is required — it is sent to the employee' : 'What needs to be fixed?'}
            onChange={e => setRet({ ...ret, text: e.target.value, bad: false })}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendReturn(); }
              if (e.key === 'Escape') { e.stopPropagation(); setRet(null); }
            }} />
        </div>
        <div className="fd-racts">
          <button type="button" className="fd-btn no" onClick={sendReturn}><UndoIcon />Send</button>
          <button type="button" className="fd-link" onClick={() => setRet(null)}>Cancel</button>
        </div>
      </div>
    );
  };

  const entryActs = (e: TsEntry) => {
    const st = stOf(e);
    if (st === 'approved') return <span className="fd-chip fd-t-green">Approved</span>;
    if (st === 'returned') return <span className="fd-chip fd-t-red">Returned</span>;
    return (
      <>
        <button type="button" className="fd-ib ok" title="Approve" aria-label="Approve" disabled={st === 'busy'}
          onClick={() => approveScope('e', String(e.id))}><CheckIcon /></button>
        <button type="button" className="fd-ib no" title="Return" aria-label="Return" disabled={st === 'busy'}
          onClick={() => setRet({ sc: 'e', id: String(e.id), text: '', bad: false })}><UndoIcon /></button>
      </>
    );
  };

  const weekInfo = (g: Group) => {
    const t = hrs(g.es);
    const contract = people[g.userId]?.contract;
    if (!contract) return { t, chip: null };
    const d = t - contract;
    const chip = d > 0.05 ? <span className="fd-chip fd-t-purple">+{f1(d)}h TOIL</span>
      : d < -0.05 ? <span className="fd-chip fd-t-red">−{f1(-d)}h</span>
      : <span className="fd-chip fd-t-green">On contract</span>;
    return { t, chip };
  };

  const dayCells = (g: Group) => [0, 1, 2, 3, 4, 5, 6].map(di => {
    const es = dayEs(g, di), h = hrs(es);
    const id = `${g.gk}#${di}`, on = pop?.gk === g.gk && pop.di === di;
    const cls = `fd-hc${!h ? ' nil' : ''}${on ? ' on' : ''}${di >= 5 ? ' we' : ''}`;
    const inten = h ? 0.06 + Math.min(h, 10) / 10 * 0.2 : 0;
    return (
      <td key={di}>
        <button type="button" className={cls} data-fdcell={id} style={{ '--fd-i': inten } as React.CSSProperties}
          title={dayDate(g.week, di).format('ddd D MMM')}
          onClick={() => { setPop(on ? null : { gk: g.gk, di }); setRet(r => (r && r.sc !== 'w' ? null : r)); }}>
          {h ? f1(h) : di >= 5 ? '·' : '—'}
          {es.length > 0 && <span className={`fd-st fd-c-${statOf(es)}`} />}
        </button>
      </td>
    );
  });

  const weekActs = (g: Group) => {
    const p = pend(g.es).length;
    const done = p === 0;
    const anyRet = g.es.some(e => stOf(e) === 'returned');
    return (
      <div className="fd-wacts">
        {done
          ? <span className={`fd-done ${anyRet ? 'no' : 'ok'}`}>{anyRet ? 'Returned' : 'Approved ✓'}</span>
          : (
            <>
              <button type="button" className="fd-ib ok" title="Approve week" aria-label="Approve week"
                onClick={() => approveScope('w', g.gk)}><DoneAllIcon /></button>
              <button type="button" className="fd-ib no" title="Return week" aria-label="Return week"
                onClick={() => { setPop(null); setRet({ sc: 'w', id: g.gk, text: '', bad: false }); }}><UndoIcon /></button>
            </>
          )}
        <Link className="fd-ib go" to={approvalsUrl(g.userId)} title="Open in Approvals" aria-label="Open in Approvals"><OpenInNewIcon /></Link>
      </div>
    );
  };

  const tsTable = () => {
    const rows = groups.map(g => {
      const info = weekInfo(g);
      const p = pend(g.es).length;
      const wk = `w/c ${moment(g.week).format('D MMM')}`;
      const name = nameOf(g.userId, g.es[0].name);
      return (
        <React.Fragment key={g.gk}>
          <tr className={p === 0 ? 'done' : ''}>
            <td>
              <div className="fd-who">
                <UserAvatar user={person(g.userId, name)} baseUrl={baseUrl} size={28} />
                <div className="fd-main"><div className="fd-t1 sm">{name}</div><div className="fd-t3">{wk} · {p}/{g.es.length} pending</div></div>
              </div>
            </td>
            {dayCells(g)}
            <td className="c"><div className="fd-hrs">{f1(info.t)}h</div>{info.chip && <div style={{ marginTop: 2 }}>{info.chip}</div>}</td>
            <td>{weekActs(g)}</td>
          </tr>
          {ret?.sc === 'w' && ret.id === g.gk && (
            <tr className="fd-rrow"><td colSpan={10}>{rbox('w', g.gk, name)}</td></tr>
          )}
        </React.Fragment>
      );
    });
    const tot = [0, 1, 2, 3, 4, 5, 6].map(di => groups.reduce((s, g) => s + hrs(dayEs(g, di)), 0));
    return (
      <div className="fd-ctw">
        <table className="fd-ct">
          <colgroup><col /><col span={7} style={{ width: 50 }} /><col style={{ width: 96 }} /><col style={{ width: 92 }} /></colgroup>
          <thead><tr><th>Employee · week</th>{DN.map(d => <th key={d}>{d}</th>)}<th>Total</th><th /></tr></thead>
          <tbody>
            {rows.length ? rows : <tr><td colSpan={10}><div className="fd-empty sm">No pending timesheets</div></td></tr>}
            {rows.length > 0 && (
              <tr className="foot"><td>Team total</td>{tot.map((h, i) => <td key={i}>{f1(h)}</td>)}
                <td>{f1(groups.reduce((s, g) => s + hrs(g.es), 0))}h</td><td /></tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const otherRow = (o: Approval) => {
    const Icon = OTHER_ICON[o.kind] || EventOutlinedIcon;
    const st = apState[o.key];
    const tone = LEAVE_KINDS.includes(o.kind) ? (o.kind === 'leave' ? 'blue' : 'amber') : 'green';
    const isLeave = LEAVE_KINDS.includes(o.kind);
    const rj = rejecting?.key === o.key ? rejecting : null;
    const name = o.userId != null ? nameOf(o.userId, o.name) : o.name;
    return (
      <React.Fragment key={o.key}>
        <div className={`fd-drw${st && st !== 'busy' ? ' done' : ''}`}>
          <UserAvatar user={person(o.userId ?? 0, name)} baseUrl={baseUrl} size={28} />
          <span className={`fd-tint fd-t-${tone}`}><Icon /></span>
          <div className="fd-main">
            <div className="fd-t1 sm">{name} · {OTHER_LABEL[o.kind]}</div>
            <div className="fd-t2">{isLeave ? <><b>{o.what}</b>{o.sub ? ` · ${o.sub}` : ''}</> : o.sub}</div>
          </div>
          {!isLeave && o.receiptUrl && (
            <a className="fd-link" href={`${baseUrl}${o.receiptUrl.replace(/^\//, '')}`} target="_blank" rel="noopener noreferrer">
              <AttachFileIcon />Receipt</a>
          )}
          {!isLeave && <span className="fd-amt">{o.what}</span>}
          <span className="fd-oacts">
            {st === 'approved' ? <span className="fd-done ok">Approved ✓</span>
              : st === 'rejected' ? <span className="fd-done no">Rejected</span>
              : (
                <>
                  <button type="button" className="fd-btn ok" disabled={st === 'busy'} onClick={() => onApprove(o)}><CheckIcon />Approve</button>
                  <button type="button" className="fd-btn no" disabled={st === 'busy'}
                    onClick={() => setRejecting(r => (r?.key === o.key ? null : { key: o.key, reason: '' }))}><CloseIcon />Reject</button>
                </>
              )}
          </span>
        </div>
        {rj && (
          <div className="fd-x fd-rj">
            <input className="fd-inp" autoFocus placeholder="Reason (optional)" value={rj.reason}
              onChange={e => setRejecting({ key: o.key, reason: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter') { setRejecting(null); onReject(o, rj.reason.trim()); } }} />
            <button type="button" className="fd-btn no solid" onClick={() => { setRejecting(null); onReject(o, rj.reason.trim()); }}>Reject</button>
            <button type="button" className="fd-link" onClick={() => setRejecting(null)}>Cancel</button>
          </div>
        )}
      </React.Fragment>
    );
  };

  // Section header = a regular feed row (same anatomy as notifications and training)
  const section = (k: 'ts' | 'lv' | 'ex', Icon: typeof EventOutlinedIcon, tone: Tone, label: string, chip: string,
    sub: string, link: { label: string; to: string }, body: React.ReactNode) => {
    const done = n[k] === 0;
    const open = sec[k] && !done;
    return (
      <FeedRow key={k} tone={tone} icon={<Icon />} title={label}
        chips={<span className={`fd-chip fd-t-${tone}`}>{chip}</span>}
        sub={done ? 'Nothing waiting for you' : sub}
        expandable={!done} open={open}
        onToggle={() => { setSec(s => ({ ...s, [k]: !s[k] })); setPop(null); }}
        meta={done ? <span className="fd-chip fd-t-green">All done ✓</span> : (
          <>
            <span className="fd-cnt">{n[k]} pending</span>
            <button type="button" className="fd-btn ok" onClick={() => approveSection(k)}><DoneAllIcon />Approve all</button>
            <Link className="fd-link" to={link.to}>{link.label}<ArrowForwardIcon /></Link>
          </>
        )}>
        {body}
      </FeedRow>
    );
  };

  // Subtitles for the section rows
  const plural = (v: number, w: string) => `${v} ${w}${v === 1 ? '' : 's'}`;
  const pendingGroups = groups.filter(g => pend(g.es).length);
  const tsSub = pendingGroups.length
    ? `${plural(new Set(pendingGroups.map(g => g.userId)).size, 'employee')} · ${plural(pendingGroups.length, 'week')} · oldest w/c ${moment(pendingGroups.map(g => g.week).sort()[0]).format('D MMM')}`
    : '';
  const lvN = leaves.filter(otherPending).length;
  const exN = claims.filter(o => o.kind === 'expense' && otherPending(o)).length;
  const miN = claims.filter(o => o.kind === 'mileage' && otherPending(o)).length;

  // ── Popover content ─────────────────────────────────────────────────────────
  const popover = () => {
    if (!pop) return null;
    const g = gFind(pop.gk);
    if (!g) return null;
    const es = dayEs(g, pop.di);
    const p = pend(es).length;
    const name = nameOf(g.userId, g.es[0].name);
    const id = `${g.gk}#${pop.di}`;
    return (
      <div className="fd-pop" ref={popRef} role="dialog" aria-label="Day entries">
        <div className="fd-ph">
          <UserAvatar user={person(g.userId, name)} baseUrl={baseUrl} size={26} />
          <div className="fd-main"><div className="fd-t1 sm">{name} · {dayDate(g.week, pop.di).format('ddd D MMM')}</div></div>
          <button type="button" className="fd-ib go" aria-label="Close" onClick={() => { setPop(null); setRet(null); }}><CloseIcon /></button>
        </div>
        <div className="fd-pb">
          {!es.length && <div className="fd-t2 pad">No time recorded.</div>}
          {es.map(e => (
            <div key={e.id} className="fd-pe">
              <div className="fd-row0">
                <span className="fd-t2 b">{e.start}–{e.end}</span>
                <b className="fd-eh">{f1(e.minutes / 60)}h</b>
                <div className="fd-sp" />
                {entryActs(e)}
              </div>
              <div className="fd-row0" style={{ marginTop: 3 }}>
                {e.category && <span className="fd-chip fd-t-purple fd-cat">{e.category}</span>}
                {e.task && <span className="fd-t1 sm">{e.task}</span>}
              </div>
              {e.description && <div className="fd-t2 wrap">{e.description}</div>}
              {rbox('e', String(e.id), name)}
            </div>
          ))}
          {rbox('d', id, name)}
        </div>
        {p > 0 && (
          <div className="fd-pf">
            <button type="button" className="fd-btn ok" onClick={() => approveScope('d', id)}><CheckIcon />Approve day</button>
            <button type="button" className="fd-btn no" onClick={() => setRet({ sc: 'd', id, text: '', bad: false })}><UndoIcon />Return day</button>
          </div>
        )}
      </div>
    );
  };


  return (
    <>
      <div className="fd-scroll" ref={scrollRef}>
        {title && (showTs || showLv || showEx) && <div className="fd-grp">{title}</div>}
        {showTs && section('ts', ScheduleOutlinedIcon, 'purple', 'Timesheet approval', 'Timesheets', tsSub,
          { label: 'Open Approvals', to: approvalsUrl() }, tsTable())}
        {showLv && section('lv', EventOutlinedIcon, 'blue', 'Leave requests', 'Absence', plural(lvN, 'request'),
          { label: 'Open Absence', to: '/hr?tab=requests' }, leaves.map(otherRow))}
        {showEx && section('ex', ReceiptLongOutlinedIcon, 'green', 'Expenses & mileage', 'Finance',
          [exN ? plural(exN, 'expense claim') : '', miN ? plural(miN, 'mileage claim') : ''].filter(Boolean).join(' · '),
          { label: 'Open Expenses', to: '/finance' }, claims.map(otherRow))}
        {after}
        <div style={{ height: 44 }} />
      </div>
      {popover()}
    </>
  );
};

export default FeedApprovals;
